import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import styles from './OwnerRecoveryScreen.module.css';

interface AccountLoadScreenProps {
  mode: 'loading' | 'connection-error' | 'switch-pending' | 'client-update-required';
  onRetry: () => void;
}

export default function AccountLoadScreen({ mode, onRetry }: AccountLoadScreenProps) {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const title = mode === 'loading'
    ? t('accountLoad.loadingTitle')
    : mode === 'client-update-required'
      ? t('accountLoad.updateTitle')
    : mode === 'switch-pending'
      ? t('accountLoad.switchTitle')
      : t('accountLoad.connectionTitle');
  const body = mode === 'loading'
    ? t('accountLoad.loadingBody')
    : mode === 'client-update-required'
      ? t('accountLoad.updateBody')
    : mode === 'switch-pending'
      ? t('accountLoad.switchBody')
      : t('accountLoad.connectionBody');

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut({ force: true });
    } catch {
      setSigningOut(false);
    }
  }

  return (
    <div className={styles.root}>
      <main className={styles.cluster}>
        {mode === 'loading' ? (
          <div
            className={styles.loadingDot}
            role="status"
            aria-label={t('accountLoad.loadingAria')}
          />
        ) : (
          <svg
            className={styles.mark}
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M12 3v10" />
            <path d="m8 9 4 4 4-4" />
            <path d="M5 17.5A3.5 3.5 0 0 0 8.5 21h7a3.5 3.5 0 0 0 0-7h-.4A5.5 5.5 0 0 0 4.7 16" />
          </svg>
        )}
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.body}>{body}</p>
        {mode !== 'loading' ? (
          <div className={styles.actions}>
            <button
              className={styles.primaryButton}
              type="button"
              disabled={signingOut}
              onClick={onRetry}
            >
              {mode === 'client-update-required'
                ? t('accountLoad.reload')
                : t('accountLoad.retry')}
            </button>
            <button
              className={styles.quietButton}
              type="button"
              disabled={signingOut}
              onClick={handleSignOut}
            >
              {signingOut ? t('accountLoad.signingOut') : t('accountLoad.signOut')}
            </button>
          </div>
        ) : null}
      </main>
    </div>
  );
}
